import { anomaliesService } from "./anomalies.service"
import { Anomalies, Anomaly, AnomalyParamsOptions } from "./models/anomalies"
import { formatDate, capitalized } from "../core/formatters"
import "./anomalies.scss"
import { DateOptionSet } from "../core/models/date"
import { ChartType } from "../charts/models/charts/chart-type.enum"
import { anomalySeriesNameFromDataObject } from "./transforms/anomalies"
import { i18n } from "../core/services/i18n-service"

export class AnomaliesComponent extends HTMLElement {
    private anomalies: Anomaly[] = []
    private type: ChartType | null = null
    constructor() {
        super()
        this.type = this.getAttribute('anomalies-type') as ChartType
    }

    connectedCallback() {
        this.Init()
    }

    public async Init(): Promise<void> {
        const anomaliesWrapper = this.createAnomaliesWrapper()

        try {
            if (!this.type) {
                this.createMissingBlocksDiv(anomaliesWrapper)
                return
            }
            const anomaliesData = await this.loadAnomaliesData(this.type)

            if (!anomaliesData || anomaliesData.length === 0) {
                this.createMissingBlocksDiv(anomaliesWrapper)
                return
            }

            this.createAnomaliesBlocks(anomaliesWrapper, anomaliesData)
        } catch (error) {
            console.error('Failed to load anomalies:', error)
            this.createMissingBlocksDiv(anomaliesWrapper)
        }

        this.appendChild(anomaliesWrapper)
    }

    private createAnomaliesWrapper(): HTMLDivElement {
        const wrapper = document.createElement('div')
        wrapper.className = 'anomalies-wrapper'
        return wrapper
    }

    private async loadAnomaliesData(type: ChartType): Promise<Anomalies[]> {
        return await anomaliesService.load(type)
    }

    private createAnomaliesBlocks(wrapper: HTMLElement, anomaliesData: Anomalies[]): void {
        anomaliesData.forEach(block => {
            const blockElement = this.createBlockElement(
                block,
            )
            wrapper.appendChild(blockElement)
        })
    }

    private createTimeDiv(blockTime: number): HTMLElement {
        const timeDiv = document.createElement('div')
        timeDiv.className = 'time-div'

        const timeElement = document.createElement('p')
        const dateLanguage = i18n.getLanguage() === 'en' ? DateOptionSet.dateEN : DateOptionSet.dateFR
        timeElement.innerText = formatDate(new Date(blockTime), dateLanguage)
        timeElement.className = 'body3 is-medium'

        const timeIcon = this.createTimeIcon()
        timeDiv.appendChild(timeIcon)
        timeDiv.appendChild(timeElement)
        return timeDiv
    }

    private createTimeIcon(): SVGSVGElement {
        const timeIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        timeIcon.setAttribute("width", "16")
        timeIcon.setAttribute("height", "17")
        timeIcon.setAttribute("viewBox", "0 0 16 17")
        timeIcon.setAttribute("fill", "none")

        timeIcon.innerHTML = `
            <path d="M5.24023 2.19952V4.16474" stroke="#161032" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.4814 2.19952V4.16474" stroke="#292D32" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2.29297 6.84399H13.4292" stroke="#292D32" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M13.7561 6.45749V12.0256C13.7561 13.9908 12.7735 15.301 10.4808 15.301H5.24021C2.94745 15.301 1.96484 13.9908 1.96484 12.0256V6.45749C1.96484 4.49227 2.94745 3.18213 5.24021 3.18213H10.4808C12.7735 3.18213 13.7561 4.49227 13.7561 6.45749Z" stroke="#292D32" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        `
        return timeIcon
    }

    private createMissingBlocksDiv(wrapper: HTMLElement): void {
        const missingBlocksDiv = document.createElement('div')
        missingBlocksDiv.className = 'anomaly-wrapper anomaly-block anomaly-item'
        missingBlocksDiv.innerHTML = `
            <p class='subheadline1'>${i18n.t('anomalies_nothing_new_here')}</p>
            <p class='body3'>${i18n.t('anomalies_come_back_to_review_anomalies')}</p>
        `
        wrapper.appendChild(missingBlocksDiv)
    }

    private createToggleButton(blockElement: HTMLElement): HTMLElement {
        const toggleButton = document.createElement('button')
        toggleButton.className = 'toggle-button'

        const anomalyScore = this.anomalies
            .filter(anomaly => !anomaly.series_name.includes('Total'))
            .reduce((acc, anomaly) => acc + anomaly.value, 0)

        const anomalyScoreIcon = anomalyScore > 0
            ? AnomalyParamsOptions.up.icon
            : AnomalyParamsOptions.down.icon

        toggleButton.innerHTML = `
            <p class='body3 toggle-button-text' style='display: none;'>
                ${this.anomalies.length} ${this.anomalies.length > 1 ? 'anomalies' : 'anomalie'}
            </p>
            <img width='21' height='21' src='${anomalyScoreIcon}' alt='Anomaly indicator'>
        `

        toggleButton.addEventListener('click', () => {
            const anomaliesList = blockElement.querySelector('.anomaly-block-bottom') as HTMLElement
            this.toggleBottomBlock(anomaliesList)
        })

        return toggleButton
    }

    private toggleBottomBlock(anomaliesList: HTMLElement): void {
        anomaliesList.classList.toggle('active')
    }

    private createAnomalyBlock(): HTMLElement {
        const anomaliesList = document.createElement('div')
        anomaliesList.className = 'anomaly-items'

        this.anomalies
            .filter(anomaly => !anomaly.series_name.includes('Total'))
            .forEach(anomaly => {
                const anomalyMessage = this.createAnomalyItem(anomaly)
                anomaliesList.appendChild(anomalyMessage)
            })

        return anomaliesList
    }

    private createAnomalyItem(anomaly: Anomaly): HTMLDivElement {
        const anomalyDivContainer = document.createElement('div')
        anomalyDivContainer.className = 'anomaly-item-container'

        const anomalyDiv = document.createElement('div')
        anomalyDiv.className = 'anomaly-item'

        const anomalyParams = anomaly.value > 0
            ? AnomalyParamsOptions.up
            : AnomalyParamsOptions.down

        const anomalyTitle = capitalized(anomalySeriesNameFromDataObject(anomaly))

        anomalyDiv.innerHTML = `
            <p class='body2'>${anomalyTitle} ${anomalyParams.text}</p>
            <img width='21' height='21' src='${anomalyParams.icon}' alt='Anomaly indicator'>
        `
        anomalyDiv.style.setProperty('--anomaly-color', anomalyParams.color)

        anomalyDivContainer.appendChild(anomalyDiv)
        return anomalyDivContainer
    }

    private createBlockElement(anomalies: Anomalies): HTMLElement {
        const blockElement = document.createElement('div')
        blockElement.className = 'anomaly-block'

        this.anomalies = anomalies.anomalies

        const top = document.createElement('div')
        top.className = 'anomaly-block-top'
        top.appendChild(this.createTimeDiv(anomalies.time))
        top.appendChild(this.createToggleButton(blockElement))
        blockElement.appendChild(top)

        const bottom = document.createElement('div')
        bottom.className = 'anomaly-block-bottom'
        bottom.appendChild(this.createAnomalyBlock())

        blockElement.appendChild(bottom)
        return blockElement
    }
}

